import { MarkerType } from '@xyflow/react';
import { getPluginDef as mockGetPluginDef } from './mock/ecaMock.js';

// ECA gateway type integer → frontend visual node id and vice-versa.
// Gateways are structural ECA constructs (type: 0/1/2), not plugins.
export const ECA_GATEWAY_TYPE_MAP = {
  0: 'gateway_and',
  1: 'gateway_or',
  2: 'gateway_xor',
};
export const FRONTEND_GATEWAY_TYPE_MAP = {
  gateway_and: 0,
  gateway_or:  1,
  gateway_xor: 2,
};

/**
 * Bidirectional converter between ECA JSON model and ReactFlow nodes/edges.
 *
 * ECA model structure:
 *   { id, label, events: {}, conditions: {}, gateways: {}, actions: {},
 *     third_party_settings: { eca_react_editor: { positions: {}, variables: {} } } }
 *
 * Each ECA node has:
 *   { plugin, label, configuration, successors: [{id, condition}] }
 * Gateway nodes also have: branches: [branchNodeId, ...]
 *
 * Variable nodes (not real ECA nodes, editor-only):
 *   ReactFlow node id:   varnode_<varId>_<refId>
 *   ReactFlow node type: 'variable'
 *   ReactFlow node data: { varId: '_var_<id>' }
 *   Stored in:           third_party_settings.eca_react_editor.variables
 *
 * Token scoping conventions (encoded in configuration fields):
 *   _wire_<srcLen>_<sourceNodeId>_<outputKey>   — unique token name for a data wire (see _wireTokenName)
 *   [_wire_<srcLen>_<sourceNodeId>_<outputKey>] — config field consuming a wired token
 *   _var_<id>                                   — tokenNameField → node writes to variable
 *   [_var_<id>]                                 — config field consuming a variable token
 *   _synth_defaults_<evtId>                     — synthetic ECA action seeding variable default values
 *
 * Positions:
 *   Stored in third_party_settings.eca_react_editor.positions (dict keyed by node ID).
 *   Backward compatible: fromEca() falls back to the legacy _position inline field.
 *
 * The two public entry points are fromEca() and toEca(); each is a thin
 * orchestrator over a sequence of single-responsibility passes (defined just
 * above it). The passes mutate shared accumulators (the nodes/edges arrays for
 * fromEca, the model object for toEca) and run in a fixed order — see each
 * orchestrator for the dependency rationale.
 */

// Regex patterns
// _var_ ids are kept short (they surface in the editor when a variable has no
// label), so the length isn't fixed here — match any non-empty id, which keeps
// the converter robust to whatever length the editor's id generator uses.
const RE_VAR_REF  = /^\[(_var_[a-z0-9]+)\]$/;
const RE_WIRE_REF  = /^\[(_wire_.+)\]$/;  // bracketed: [_wire_nodeId_outputKey]
const RE_WIRE_BARE = /^(_wire_.+)$/;       // bare:      _wire_nodeId_outputKey (tokenRef fields)
const SYNTH_DEFAULTS_PREFIX = '_synth_defaults_'; // consolidated: eca_extras_set_multiple_token_values (variable defaults)
const SYNTH_TOKENS_PREFIX = '_synth_tokens_';     // eca_extras_set_multiple_token_values (wires + varsets)
// Three-gateway pattern per condition node c:
//   _synth_in_c    — input buffer; upstream → here (no condition), then routes via c / c_neg filter
//   _synth_true_c  — TRUE dispatch; receives flow when c is TRUE, fans out to all TRUE targets (condition: '')
//   _synth_false_c — FALSE dispatch; receives flow when c is FALSE, fans out to all FALSE targets (condition: '')
// Keeping the condition filter on at most ONE successor per branch prevents "element already exists" conflicts.
const SYNTH_IN_PREFIX    = '_synth_in_';
const SYNTH_TRUE_PREFIX  = '_synth_true_';
const SYNTH_FALSE_PREFIX = '_synth_false_';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const fluxEdge = (id, source, target, sourceHandle, targetHandle) => ({
  id,
  source,
  target,
  sourceHandle,
  targetHandle,
  type: 'flux',
  markerEnd: { type: MarkerType.ArrowClosed, color: '#3B82F6' },
  style: { stroke: '#3B82F6', strokeWidth: 2.5 },
});

const dataEdge = (id, source, target, sourceHandle, targetHandle) => ({
  id,
  source,
  target,
  sourceHandle,
  targetHandle,
  type: 'data',
});

/** Looks up a node in any section of the ECA model. */
const findInModel = (model, nodeId) =>
  model.events?.[nodeId] ?? model.conditions?.[nodeId] ??
  model.gateways?.[nodeId] ?? model.actions?.[nodeId] ?? null;

/**
 * Unique token name for a data wire.
 *
 *   _wire_<sourceIdLength>_<sourceId>_<outputKey>
 *
 * The source-id length pins the (sourceId, outputKey) boundary, so two distinct
 * wires can NEVER alias onto the same token name even when node ids contain
 * underscores or are prefixes of one another. Aliasing would otherwise make two
 * wires share a single token at ECA runtime (last writer wins). The name need
 * not be parsed back — reconstruction resolves source/key authoritatively from
 * the _synth_tokens_ action (see _buildWireIndex) — it only has to be unique.
 */
const _wireTokenName = (sourceId, outputKey) => `_wire_${sourceId.length}_${sourceId}_${outputKey}`;

// ===========================================================================
// ECA JSON → ReactFlow  (fromEca)
// ===========================================================================

/**
 * Resolve the real (non-synthetic) successors of a synthetic action by
 * recursively following the chain of synthetic prefixes.
 *
 * Handles arbitrarily deep chains such as:
 *   event → _synth_defaults_ → _synth_tokens_ → _synth_in_ → condition
 *
 * @param {object} model  ECA model (needs model.actions)
 * @param {string} synthId  ID of the synthetic action to follow through
 * @returns {Array<{id:string, condition:string}>} Real successor entries
 */
const _resolveRealSuccessors = (model, synthId) => {
  const action = model.actions?.[synthId];
  if (!action) return [];
  const result = [];
  for (const s of action.successors ?? []) {
    if (s.id.startsWith(SYNTH_TOKENS_PREFIX) || s.id.startsWith(SYNTH_DEFAULTS_PREFIX)) {
      result.push(..._resolveRealSuccessors(model, s.id));
    } else {
      result.push(s);
    }
  }
  return result;
};

/**
 * Build a ReactFlow flux edge for a single ECA successor entry.
 *
 * All current-format successors carry `condition: ''` — conditions are encoded
 * via the three-gateway pattern (_synth_in_/_synth_true_/_synth_false_), not as
 * edge filters on real nodes. The only special case here is a `_synth_in_*`
 * target: that buffer gateway is never rendered, so we draw the
 * upstream → condition edge directly.
 *
 * @param {Array}  edges          Accumulator to push into.
 * @param {string} sourceId       ID of the upstream node.
 * @param {object} successor      ECA successor entry { id, condition }.
 * @param {string} sourceHandle   Handle to use on the upstream node.
 * @param {string} [targetHandle] Handle to use on the final target (default 'flux-in').
 * @param {Set}    [seenEdgeIds]  Optional dedup set shared across calls for the same source.
 */
const _buildSuccessorEdges = (edges, sourceId, successor, sourceHandle, targetHandle = 'flux-in', seenEdgeIds = new Set()) => {
  const targetId = successor.id;

  // _synth_in_* target → draw the upstream → condition edge directly.
  if (targetId.startsWith(SYNTH_IN_PREFIX)) {
    const condNodeId = targetId.slice(SYNTH_IN_PREFIX.length);
    const edgeId = `e_${sourceId}_${condNodeId}`;
    if (!seenEdgeIds.has(edgeId)) {
      seenEdgeIds.add(edgeId);
      edges.push(fluxEdge(edgeId, sourceId, condNodeId, sourceHandle, 'flux-in'));
    }
    return;
  }

  edges.push(fluxEdge(`e_${sourceId}_${targetId}`, sourceId, targetId, sourceHandle, targetHandle));
};

/** Build the `data` payload shared by event and action nodes. */
const _pluginNodeData = (node, id, resolvePlugin, category) => {
  const pluginDef = resolvePlugin(category, node.plugin);
  return {
    label: node.label ?? id,
    plugin: node.plugin,
    description: pluginDef?.description ?? '',
    configuration: { ...node.configuration },
    tokenOutputs: pluginDef?.tokenOutputs ?? [],
  };
};

/** Target handle for an edge into `gatewayId`'s branch from `sourceId`, else 'flux-in'. */
const _gatewayBranchHandle = (model, gatewayId, sourceId) => {
  const idx = model.gateways?.[gatewayId]?.branches?.indexOf(sourceId) ?? -1;
  return idx >= 0 ? `flux-in-branch-${idx}` : 'flux-in';
};

/**
 * Emit flux edges for a node's successors, transparently following through any
 * chain of synthetic actions (_synth_tokens_ / _synth_defaults_) so the real
 * downstream node is reached.
 *
 * @param {Function} targetHandleFor (realTargetId) => targetHandle string
 */
const _emitSuccessorFluxEdges = (model, edges, sourceId, successors, targetHandleFor) => {
  for (const s of successors ?? []) {
    const realSuccessors =
      s.id.startsWith(SYNTH_TOKENS_PREFIX) || s.id.startsWith(SYNTH_DEFAULTS_PREFIX)
        ? _resolveRealSuccessors(model, s.id)
        : [s];
    for (const ss of realSuccessors) {
      _buildSuccessorEdges(edges, sourceId, ss, 'flux-out', targetHandleFor(ss.id));
    }
  }
};

/** Event nodes + their outgoing flux edges. */
const _buildEventNodes = (model, nodes, edges, resolvePlugin, nodePosition) => {
  for (const [id, node] of Object.entries(model.events ?? {})) {
    nodes.push({ id, type: 'event', position: nodePosition(id, node), data: _pluginNodeData(node, id, resolvePlugin, 'events') });
    _emitSuccessorFluxEdges(model, edges, id, node.successors, () => 'flux-in');
  }
};

/**
 * Condition nodes (visual only). Outgoing edges are reconstructed later from
 * the dispatch gateways. Synthetic negated conditions (condId_neg) are an ECA
 * encoding detail and are skipped — they reappear as FALSE-branch edges.
 */
const _buildConditionNodes = (model, nodes, nodePosition) => {
  for (const [id, node] of Object.entries(model.conditions ?? {})) {
    if (id.endsWith('_neg') && model.conditions[id.slice(0, -4)]) continue;
    nodes.push({
      id,
      type: 'condition',
      position: nodePosition(id, node),
      data: { label: node.label ?? id, plugin: node.plugin, configuration: { ...node.configuration } },
    });
  }
};

/**
 * Gateway nodes + their outgoing flux edges. The _synth_in_/_synth_true_/
 * _synth_false_ gateways are condition-encoding artefacts and are not rendered.
 */
const _buildGatewayNodes = (model, nodes, edges, nodePosition) => {
  for (const [id, node] of Object.entries(model.gateways ?? {})) {
    if (id.startsWith(SYNTH_IN_PREFIX) || id.startsWith(SYNTH_TRUE_PREFIX) || id.startsWith(SYNTH_FALSE_PREFIX)) continue;
    const ecaType = node.type ?? 0;
    nodes.push({
      id,
      type: 'gateway',
      position: nodePosition(id, node),
      data: {
        label:       node.label ?? id,
        gatewayType: ECA_GATEWAY_TYPE_MAP[ecaType] ?? 'gateway_and',
        ecaType,
        configuration: { ...node.configuration },
        branches: node.branches ?? [],
      },
    });
    const seenGwEdges = new Set();
    for (const s of node.successors ?? []) {
      _buildSuccessorEdges(edges, id, s, 'flux-out', 'flux-in', seenGwEdges);
    }
  }
};

/** Action nodes + their outgoing flux edges (with gateway-branch target handles). */
const _buildActionNodes = (model, nodes, edges, resolvePlugin, nodePosition) => {
  for (const [id, node] of Object.entries(model.actions ?? {})) {
    if (id.startsWith(SYNTH_DEFAULTS_PREFIX) || id.startsWith(SYNTH_TOKENS_PREFIX)) continue;
    nodes.push({ id, type: 'action', position: nodePosition(id, node), data: _pluginNodeData(node, id, resolvePlugin, 'actions') });
    _emitSuccessorFluxEdges(model, edges, id, node.successors, (targetId) => _gatewayBranchHandle(model, targetId, id));
  }
};

/**
 * Reconstruct a condition's TRUE/FALSE output edges from its dispatch gateways
 * (_synth_true_c / _synth_false_c). The input edge (upstream → condition) is
 * already drawn by _buildSuccessorEdges when it meets the `_synth_in_c`
 * successor on the upstream node.
 */
const _reconstructConditionBranchEdges = (model, edges) => {
  const dispatches = [
    { prefix: SYNTH_TRUE_PREFIX,  handle: 'flux-out-true',  suffix: 'true'  },
    { prefix: SYNTH_FALSE_PREFIX, handle: 'flux-out-false', suffix: 'false' },
  ];
  for (const { prefix, handle, suffix } of dispatches) {
    for (const [gwId, gwNode] of Object.entries(model.gateways ?? {})) {
      if (!gwId.startsWith(prefix)) continue;
      const condId = gwId.slice(prefix.length);
      for (const s of gwNode.successors ?? []) {
        const targetNodeId = s.id.startsWith(SYNTH_IN_PREFIX) ? s.id.slice(SYNTH_IN_PREFIX.length) : s.id;
        const eid = `e_${condId}_${targetNodeId}_${suffix}`;
        if (!edges.some(e => e.id === eid)) {
          edges.push(fluxEdge(eid, condId, targetNodeId, handle, 'flux-in'));
        }
      }
    }
  }
};

/** Variable nodes — one ReactFlow node per reference. */
const _buildVariableNodes = (variables, nodes) => {
  for (const [varId, varMeta] of Object.entries(variables)) {
    for (const ref of varMeta.references ?? []) {
      nodes.push({
        id: `varnode_${varId}_${ref.ref_id}`,
        type: 'variable',
        position: ref.position ?? { x: 0, y: 0 },
        data: { varId },
      });
    }
  }
};

/**
 * Reconstruct variable setter edges (ECA node → variable) from the `_var_*`
 * lines of each _synth_tokens_{sourceId} action's token_mapping. The `_wire_*`
 * lines are reconstructed from config field scanning instead, so they're skipped.
 */
const _reconstructVariableSetterEdges = (model, nodes, edges) => {
  for (const [actionId, action] of Object.entries(model.actions ?? {})) {
    if (!actionId.startsWith(SYNTH_TOKENS_PREFIX)) continue;
    const sourceId = actionId.slice(SYNTH_TOKENS_PREFIX.length);
    for (const line of (action.configuration?.token_mapping ?? '').split('\n')) {
      const sep = line.indexOf('|');
      if (sep < 0) continue;
      const tokenName = line.slice(0, sep).trim();   // '_var_abc123' or '_wire_...'
      if (!tokenName.startsWith('_var_')) continue;
      const outputKey = line.slice(sep + 1).trim().replace(/^\[(.+)\]$/, '$1'); // '[entity]' → 'entity'
      const varNode   = nodes.find(n => n.type === 'variable' && n.data.varId === tokenName);
      if (!varNode) continue;
      edges.push(dataEdge(`data_${sourceId}_${varNode.id}_varset`, sourceId, varNode.id, `data-out-${outputKey}`, 'var-in'));
    }
  }
};

/**
 * Build an authoritative index of every data wire from the `_synth_tokens_`
 * actions toEca writes: tokenName ('_wire_src_key') → { sourceId, outputKey }.
 *
 * Both fields are read unambiguously — the sourceId from the action id
 * (`_synth_tokens_<sourceId>`) and the outputKey from the bracketed RHS of the
 * mapping line (`_wire_src_key|[key]`) — so node IDs that contain underscores
 * (or are prefixes of one another) can never be mis-attributed.
 */
const _buildWireIndex = (model) => {
  const index = new Map();
  for (const [actionId, action] of Object.entries(model.actions ?? {})) {
    if (!actionId.startsWith(SYNTH_TOKENS_PREFIX)) continue;
    const sourceId = actionId.slice(SYNTH_TOKENS_PREFIX.length);
    for (const line of (action.configuration?.token_mapping ?? '').split('\n')) {
      const sep = line.indexOf('|');
      if (sep < 0) continue;
      const tokenName = line.slice(0, sep).trim();
      if (!tokenName.startsWith('_wire_')) continue;
      const outputKey = line.slice(sep + 1).trim().replace(/^\[(.+)\]$/, '$1');
      index.set(tokenName, { sourceId, outputKey });
    }
  }
  return index;
};

/**
 * Reconstruct data edges from token patterns in configuration fields:
 *   [_var_<id>]                  → variable → ECA node (getter edge)
 *   [_wire_<srcId>_<outputKey>]  → direct data wire (standard ECA node → node)
 *   _wire_<srcId>_<outputKey>    → idem, bare form, only on tokenRef fields
 *
 * Wire (sourceId, outputKey) is resolved from the authoritative `_synth_tokens_`
 * index, never by parsing the token string itself.
 */
const _reconstructDataFieldEdges = (model, nodes, edges, resolvePlugin) => {
  const wireIndex = _buildWireIndex(model);
  const allStdNodeIds = [
    ...Object.keys(model.events ?? {}),
    ...Object.keys(model.conditions ?? {}),
    ...Object.keys(model.actions ?? {}),
  ].filter(id => !id.startsWith(SYNTH_DEFAULTS_PREFIX) && !id.startsWith(SYNTH_TOKENS_PREFIX));

  for (const nodeId of allStdNodeIds) {
    const nodeData = findInModel(model, nodeId);
    if (!nodeData) continue;
    for (const [fieldKey, fieldValue] of Object.entries(nodeData.configuration ?? {})) {
      if (typeof fieldValue !== 'string') continue;

      // --- Variable getter: [_var_<id>] ---
      const varRefMatch = fieldValue.match(RE_VAR_REF);
      if (varRefMatch) {
        const varNode = nodes.find(n => n.type === 'variable' && n.data.varId === varRefMatch[1]);
        if (varNode) {
          edges.push(dataEdge(`data_${varNode.id}_${nodeId}_${fieldKey}`, varNode.id, nodeId, 'var-out', `data-in-${fieldKey}`));
        }
        continue;
      }

      // --- Wire getter: [_wire_…] (standard fields) or _wire_… (bare, tokenRef fields) ---
      const wireRefMatch  = fieldValue.match(RE_WIRE_REF);
      let   wireBareMatch = !wireRefMatch && fieldValue.match(RE_WIRE_BARE);
      // A bare _wire_* is a getter only on tokenRef fields; otherwise it is a
      // setter value (e.g. token_name on the source node) — skip.
      if (wireBareMatch) {
        const category    = model.events?.[nodeId] ? 'events' : model.conditions?.[nodeId] ? 'conditions' : 'actions';
        const fieldSchema = resolvePlugin(category, nodeData.plugin)?.configSchema?.find(f => f.key === fieldKey);
        if (!fieldSchema?.tokenRef) wireBareMatch = null;
      }
      const wireMatch = wireRefMatch ?? wireBareMatch;
      if (!wireMatch) continue;

      const wire = wireIndex.get(wireMatch[1]);
      if (!wire) continue; // orphan reference with no authoritative source — skip
      edges.push(dataEdge(`data_${wire.sourceId}_${nodeId}_${fieldKey}`, wire.sourceId, nodeId, `data-out-${wire.outputKey}`, `data-in-${fieldKey}`));
    }
  }
};

/**
 * Convert an ECA JSON model to ReactFlow nodes + edges.
 *
 * Passes run in this order because of their data dependencies: all real nodes
 * (incl. variable nodes) must exist before the data passes that look them up.
 *
 * @param {object} model     ECA config entity (events, conditions, gateways, actions…)
 * @param {object} [options]
 * @param {Function} [options.getPluginDef]  (category, pluginId) → pluginDef | null
 *   When provided (from live catalogue), used instead of the mock fallback.
 */
export const fromEca = (model, { getPluginDef } = {}) => {
  const resolvePlugin = getPluginDef ?? mockGetPluginDef;
  const nodes = [];
  const edges = [];

  const editorData = model.third_party_settings?.eca_react_editor ?? {};
  const variables  = editorData.variables ?? {};
  const positions  = editorData.positions ?? {};
  const nodePosition = (nodeId, node) => positions[nodeId] ?? node._position ?? { x: 0, y: 0 };

  // 1. Standard nodes + their flux edges.
  _buildEventNodes(model, nodes, edges, resolvePlugin, nodePosition);
  _buildConditionNodes(model, nodes, nodePosition);
  _buildGatewayNodes(model, nodes, edges, nodePosition);
  _buildActionNodes(model, nodes, edges, resolvePlugin, nodePosition);

  // 2. Condition TRUE/FALSE output edges from the dispatch gateways.
  _reconstructConditionBranchEdges(model, edges);

  // 3. Variable nodes, then their data edges (setters then getters/wires).
  _buildVariableNodes(variables, nodes);
  _reconstructVariableSetterEdges(model, nodes, edges);
  _reconstructDataFieldEdges(model, nodes, edges, resolvePlugin);

  return { nodes, edges, variables };
};

// ===========================================================================
// ReactFlow → ECA JSON  (toEca)
// ===========================================================================

/** Register standard (non-variable) nodes into the model and collect positions. */
const _registerStandardNodes = (nodes, model, positionsDict) => {
  for (const node of nodes) {
    if (node.type === 'variable') continue;

    positionsDict[node.id] = { x: Math.round(node.position.x), y: Math.round(node.position.y) };

    const base = {
      plugin: node.data.plugin ?? '',
      label: node.data.label ?? node.id,
      configuration: { ...node.data.configuration },
      successors: [],
    };

    if (node.type === 'event')          model.events[node.id]     = base;
    else if (node.type === 'condition') model.conditions[node.id] = { plugin: base.plugin, label: base.label, configuration: base.configuration };
    else if (node.type === 'gateway') {
      // Map frontend visual type → ECA integer type (0=AND, 1=OR, 2=XOR).
      const ecaType = node.data.ecaType
        ?? FRONTEND_GATEWAY_TYPE_MAP[node.data.gatewayType]
        ?? FRONTEND_GATEWAY_TYPE_MAP[node.data.plugin]  // legacy fallback
        ?? 0;
      // ECA gateways use `type` (integer), not `plugin`.
      const { plugin: _unused, ...baseWithoutPlugin } = base;
      model.gateways[node.id] = { ...baseWithoutPlugin, type: ecaType, branches: [...(node.data.branches ?? [])] };
    }
    else if (node.type === 'action')    model.actions[node.id]    = base;
  }
};

/**
 * Index each condition node's connected flux edges.
 *
 * @returns {Object<string, {inEdges:Array, trueEdges:Array, falseEdges:Array}>}
 *   Keyed by condition node id. `trueEdges`/`falseEdges` are the condition's
 *   outgoing edges split by the `flux-out-false` source handle.
 */
const _indexConditionConnections = (nodes, edges) => {
  const condConn = {};
  for (const node of nodes) {
    if (node.type === 'condition') condConn[node.id] = { inEdges: [], trueEdges: [], falseEdges: [] };
  }
  for (const edge of edges) {
    if (edge.type !== 'flux') continue;
    if (condConn[edge.target]) condConn[edge.target].inEdges.push(edge);
    if (condConn[edge.source]) {
      const bucket = edge.sourceHandle === 'flux-out-false' ? 'falseEdges' : 'trueEdges';
      condConn[edge.source][bucket].push(edge);
    }
  }
  return condConn;
};

/**
 * Create the three-gateway pattern for every condition:
 *   _synth_in_c   — input buffer (always), routes to TRUE/FALSE dispatch by filter
 *   _synth_true_c — created when c has ≥1 TRUE out-edge
 *   _synth_false_c— created when c has ≥1 FALSE out-edge (+ the c_neg condition)
 *
 * Each _synth_in_c successor carries a UNIQUE condition filter (c or c_neg), so
 * no "element already exists" conflict can occur.
 */
const _createConditionGateways = (model, nodes, condConn) => {
  for (const [condId, conn] of Object.entries(condConn)) {
    const inId = `${SYNTH_IN_PREFIX}${condId}`;
    // Input buffer — always present so upstream connections survive even with no outputs.
    model.gateways[inId] = { label: `[synth_in] ${condId}`, type: 0, successors: [] };

    if (conn.trueEdges.length > 0) {
      model.gateways[`${SYNTH_TRUE_PREFIX}${condId}`] = { label: `[synth_true] ${condId}`, type: 0, successors: [] };
      model.gateways[inId].successors.push({ id: `${SYNTH_TRUE_PREFIX}${condId}`, condition: condId });
    }

    if (conn.falseEdges.length > 0) {
      model.gateways[`${SYNTH_FALSE_PREFIX}${condId}`] = { label: `[synth_false] ${condId}`, type: 0, successors: [] };
      const negId = `${condId}_neg`;
      model.gateways[inId].successors.push({ id: `${SYNTH_FALSE_PREFIX}${condId}`, condition: negId });
      // Create the negated condition in the model.
      const condNode = nodes.find(n => n.id === condId);
      if (condNode) {
        model.conditions[negId] = {
          plugin: condNode.data.plugin,
          label: `${condNode.data.label || condId} (negated)`,
          configuration: { ...(condNode.data.configuration ?? {}), negate: true },
        };
      }
    }
  }
};

/**
 * Wire each flux edge into successors / dispatch gateways:
 *   Non-condition → condition:     upstream.successors += {_synth_in_c, ''}
 *   Condition(T/F) → target:       _synth_(true|false)_c.successors += {target, ''}
 *   Non-condition → non-condition: source.successors += {target, ''}
 */
const _wireFluxEdges = (model, edges, condConn) => {
  for (const edge of edges) {
    if (edge.type !== 'flux') continue;
    const srcIsCond = Boolean(condConn[edge.source]);
    const tgtIsCond = Boolean(condConn[edge.target]);
    const isFalse   = edge.sourceHandle === 'flux-out-false';

    if (!srcIsCond && tgtIsCond) {
      // Non-condition → condition: point upstream to the input buffer.
      const upstreamNode = findInModel(model, edge.source);
      if (!upstreamNode) continue;
      const synthInId = `${SYNTH_IN_PREFIX}${edge.target}`;
      if (!upstreamNode.successors.some(s => s.id === synthInId && s.condition === '')) {
        upstreamNode.successors.push({ id: synthInId, condition: '' });
      }
    } else if (srcIsCond) {
      // Condition → something: add target to the appropriate dispatch gateway.
      const dispatchId = isFalse ? `${SYNTH_FALSE_PREFIX}${edge.source}` : `${SYNTH_TRUE_PREFIX}${edge.source}`;
      if (!model.gateways[dispatchId]) continue; // guard: shouldn't happen
      // When the target is another condition, route to its input buffer.
      const targetId = tgtIsCond ? `${SYNTH_IN_PREFIX}${edge.target}` : edge.target;
      if (!model.gateways[dispatchId].successors.some(s => s.id === targetId && s.condition === '')) {
        model.gateways[dispatchId].successors.push({ id: targetId, condition: '' });
      }
    } else {
      // Non-condition → non-condition: direct successor.
      const sourceNode = findInModel(model, edge.source);
      if (!sourceNode) continue;
      // Ignore stale flux edges targeting synthetic nodes (fromEca artefacts from a
      // previous round-trip). They are reconstructed by the data pass, so
      // re-injecting them here would create a self-referencing loop.
      if (edge.target.startsWith(SYNTH_TOKENS_PREFIX) || edge.target.startsWith(SYNTH_DEFAULTS_PREFIX)) continue;
      if (!sourceNode.successors.some(s => s.id === edge.target && s.condition === '')) {
        sourceNode.successors.push({ id: edge.target, condition: '' });
      }
    }
  }
};

/**
 * Process data edges into token wiring.
 *   A1. Variable → ECA node (getter):  target.config[param] = [_var_xxx]
 *   A2. ECA node → Variable (setter):  _synth_tokens_ line  _var_xxx|[outputKey]
 *   B.  ECA node → ECA node (wire):    _synth_tokens_ line  _wire_src_key|[key]
 *                                      + target.config[param] = [_wire_…] (or bare on tokenRef)
 *
 * All token assignments from a given source are grouped into a single
 * eca_extras_set_multiple_token_values action (_synth_tokens_{sourceId}),
 * spliced before the original flux chain so tokens are available downstream.
 */
const _processDataEdges = (model, nodes, edges, getPluginDef) => {
  // Append one "tokenName|[outputKey]" line to _synth_tokens_{sourceId},
  // creating it (and stealing the source's current successors) on first use.
  const addSynthTokenLine = (sourceId, tokenName, outputKey) => {
    const sourceSection = findInModel(model, sourceId);
    if (!sourceSection) return;
    const synthId = `${SYNTH_TOKENS_PREFIX}${sourceId}`;
    if (!model.actions[synthId]) {
      const stolen = [...(sourceSection.successors ?? [])];
      model.actions[synthId] = {
        plugin: 'eca_extras_set_multiple_token_values',
        label: `[tokens] ${sourceId}`,
        configuration: { token_mapping: '' },
        successors: stolen,
      };
      sourceSection.successors = [{ id: synthId, condition: '' }];
    }
    const line = `${tokenName}|[${outputKey}]`;
    const prev = model.actions[synthId].configuration.token_mapping;
    model.actions[synthId].configuration.token_mapping = prev ? `${prev}\n${line}` : line;
  };

  for (const edge of edges) {
    if (edge.type !== 'data') continue;

    const sourceNode = nodes.find(n => n.id === edge.source);
    const targetNode = nodes.find(n => n.id === edge.target);
    if (!sourceNode || !targetNode) continue;

    // --- Case A1: Variable → ECA node (getter) ---
    if (sourceNode.type === 'variable') {
      const paramKey = edge.targetHandle?.replace('data-in-', '');
      if (!paramKey) continue;
      const targetSection = findInModel(model, edge.target);
      if (targetSection) targetSection.configuration[paramKey] = `[${sourceNode.data.varId}]`;
      continue;
    }

    // --- Case A2: ECA node → Variable (setter) ---
    // eca_extras_set_multiple_token_values resolves [outputKey] via getOrReplace,
    // preserving entity objects (unlike eca_token_set_value which renders strings).
    if (targetNode.type === 'variable') {
      const outputKey = edge.sourceHandle?.replace('data-out-', '');
      if (!outputKey) continue;
      addSynthTokenLine(edge.source, targetNode.data.varId, outputKey);
      continue;
    }

    // --- Case B: Standard ECA node → Standard ECA node (direct data wire) ---
    const outputKey     = edge.sourceHandle?.replace('data-out-', '');
    const paramKey      = edge.targetHandle?.replace('data-in-', '');
    const targetSection = findInModel(model, edge.target);
    if (!outputKey || !paramKey || !targetSection) continue;

    const tokenName = _wireTokenName(edge.source, outputKey);
    addSynthTokenLine(edge.source, tokenName, outputKey);

    // Inject the token reference into the target field. tokenRef fields (ECA
    // #eca_token_reference) expect the bare token name; others use [token].
    const targetCategory = model.conditions?.[edge.target] ? 'conditions'
      : model.actions?.[edge.target] ? 'actions'
      : model.events?.[edge.target] ? 'events'
      : null;
    const targetPlugin    = targetSection.plugin ?? targetNode.data?.plugin;
    const targetPluginDef = targetCategory ? getPluginDef(targetCategory, targetPlugin) : null;
    const isTokenRef      = targetPluginDef?.configSchema?.find(f => f.key === paramKey)?.tokenRef ?? false;

    targetSection.configuration[paramKey] = isTokenRef ? tokenName : `[${tokenName}]`;
  }
};

/**
 * Sync negated condition configurations after the data pass.
 *
 * `_neg` copies are created during the flux pass from the condition node's raw
 * configuration, but the data pass may then inject wired field values into the
 * positive condition. Without this sync those values would be missing from the
 * `_neg` copy, breaking FALSE-branch evaluation.
 */
const _syncNegatedConditions = (model, condConn) => {
  for (const condId of Object.keys(condConn)) {
    const negId = `${condId}_neg`;
    if (!model.conditions[condId] || !model.conditions[negId]) continue;
    model.conditions[negId].configuration = {
      ...model.conditions[condId].configuration,
      negate: true, // the _neg copy must always negate, regardless of original
    };
  }
};

/**
 * Inject a synthetic _synth_defaults_{evtId} action per event for variable
 * default values (consolidated eca_extras_set_multiple_token_values).
 *
 * The default is injected for EVERY event regardless of whether a setter edge
 * also exists; the setter (fired later) overrides it, giving a fallback semantic.
 */
const _injectVariableDefaults = (model, varNodes, variables) => {
  // Unique varIds that carry a default value.
  const defaultVarEntries = [
    ...new Map(
      varNodes
        .filter(n => (variables[n.data.varId] ?? {}).defaultValue)
        .map(n => [n.data.varId, n])
    ).values(),
  ];
  if (defaultVarEntries.length === 0) return;

  const tokenMapping = defaultVarEntries
    .map(n => `${n.data.varId}|${(variables[n.data.varId] ?? {}).defaultValue}`)
    .join('\n');

  for (const [evtId, evt] of Object.entries(model.events)) {
    const synthId = `${SYNTH_DEFAULTS_PREFIX}${evtId}`;
    model.actions[synthId] = {
      plugin:        'eca_extras_set_multiple_token_values',
      label:         `[defaults] ${evtId}`,
      configuration: { token_mapping: tokenMapping },
      successors:    [...(evt.successors ?? [])],
    };
    evt.successors = [{ id: synthId, condition: '' }];
  }
};

/**
 * Encode positions + variable metadata into third_party_settings.eca_react_editor.
 *
 * `editorExtras` = les clés que l'éditeur ne produit PAS mais qui vivent au même
 * endroit (aujourd'hui `categories`, écrit par la liste des modèles). Sans ce
 * report, chaque sauvegarde depuis l'éditeur les effacerait : cette fonction
 * REMPLACE `third_party_settings`.
 */
const _encodeEditorSettings = (model, varNodes, variables, positionsDict, modelLabel = '', editorExtras = {}) => {
  const varRefs = {};
  for (const node of varNodes) {
    const { varId } = node.data;
    if (!varRefs[varId]) varRefs[varId] = [];
    // Strip the varnode_${varId}_ prefix so ref_id stays stable across round-trips.
    const nodePrefix = `varnode_${varId}_`;
    const refId = node.id.startsWith(nodePrefix) ? node.id.slice(nodePrefix.length) : node.id;
    varRefs[varId].push({ ref_id: refId, position: { x: Math.round(node.position.x), y: Math.round(node.position.y) } });
  }

  const varMeta = {};
  for (const [varId, refs] of Object.entries(varRefs)) {
    const meta = variables[varId] ?? {};
    varMeta[varId] = {
      label:        meta.label        ?? '',
      color:        meta.color        ?? '#8B5CF6',
      defaultValue: meta.defaultValue ?? '',
      references:   refs,
    };
  }

  model.third_party_settings = {
    // Claim this flow for the React modeler so the Drupal admin "edit" link
    // (modeler_api) opens it in React, never in BPMN.iO. Without this every
    // SPA-saved flow resolves to the 'fallback' modeler. The label MUST live
    // here too: Eca::label() reads third_party_settings.modeler_api.label and
    // falls back to the literal 'undefined' when absent (the top-level `label`
    // key is not part of the ECA config_export and is dropped on save). See
    // docs/frontend/eca-react-editor-architecture.md.
    modeler_api: {
      modeler_id: 'eca_react',
      label: modelLabel,
    },
    eca_react_editor: {
      ...editorExtras,
      positions: positionsDict,
      ...(Object.keys(varMeta).length > 0 ? { variables: varMeta } : {}),
    },
  };
};

/**
 * Convert ReactFlow nodes + edges to an ECA JSON model.
 *
 * Pass order matters: nodes are registered first; condition gateways are
 * created before flux edges are wired into them; the data pass runs before the
 * negated-condition sync (which copies injected field values); defaults and
 * editor settings are encoded last.
 */
export const toEca = ({
  nodes,
  edges,
  variables = {},
  modelId = 'exported_model',
  modelLabel = 'Exported ECA model',
  getPluginDef = () => null,
  editorExtras = {},
}) => {
  const model = { id: modelId, label: modelLabel, events: {}, conditions: {}, gateways: {}, actions: {} };
  const positionsDict = {};

  _registerStandardNodes(nodes, model, positionsDict);

  // Flux wiring (three-gateway condition encoding).
  const condConn = _indexConditionConnections(nodes, edges);
  _createConditionGateways(model, nodes, condConn);
  _wireFluxEdges(model, edges, condConn);

  // Data wiring + dependent fix-ups.
  _processDataEdges(model, nodes, edges, getPluginDef);
  _syncNegatedConditions(model, condConn);

  // Variable defaults + editor metadata.
  const varNodes = nodes.filter(n => n.type === 'variable');
  _injectVariableDefaults(model, varNodes, variables);
  _encodeEditorSettings(model, varNodes, variables, positionsDict, modelLabel, editorExtras);

  return model;
};

// ---------------------------------------------------------------------------
// Internal passes exposed for unit testing only — NOT part of the public API.
// The supported surface is fromEca() / toEca(); these may change without notice.
// ---------------------------------------------------------------------------
export const _internals = {
  // fromEca passes
  _buildEventNodes,
  _buildConditionNodes,
  _buildGatewayNodes,
  _buildActionNodes,
  _reconstructConditionBranchEdges,
  _buildVariableNodes,
  _reconstructVariableSetterEdges,
  _reconstructDataFieldEdges,
  _buildWireIndex,
  _wireTokenName,
  // toEca passes
  _registerStandardNodes,
  _indexConditionConnections,
  _createConditionGateways,
  _wireFluxEdges,
  _processDataEdges,
  _syncNegatedConditions,
  _injectVariableDefaults,
  _encodeEditorSettings,
};
